import { RefObject, useLayoutEffect, useRef } from 'react'
import type { IOrdinalProvenanceActor } from 'api/api'
import { useOrdinalProvenance } from 'components/answers/ordinalProvenance'
import { withDepth } from 'components/filters/useStreamFilters'
import {
    OrdinalBridgeActor,
    OrdinalContextPayload,
    registerOrdinalFrame,
} from 'lib/ordinalWalletBridge/host'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useStore, { resolveEffectiveTheme } from 'state/knovStore'

export interface OrdinalWalletBridgeProps {
    origin: string
    answerId?: string | null
    threadId?: string | null
    /** Called when another ordinal starts and this one has to stop. */
    onStop?: () => void
}

function actor(value: IOrdinalProvenanceActor | null | undefined): OrdinalBridgeActor | null {
    if (!value) return null
    const username = value.user?.name || value.user?.username || null
    if (!username && !value.address) return null
    return { username, address: value.address || null }
}

// Lets the live HTML ordinal in `iframeRef` call the viewer's Treechat wallet over
// postMessage (BRC-100 XDM plus Treechat's own calls: getContext, getViewer, getUsers,
// openProfile and openPost). The frame mounts without a src: registering stops any other
// running ordinal first, and only then does this one load, so the other never shares the page
// with it.
export function useOrdinalWalletBridge(
    iframeRef: RefObject<HTMLIFrameElement | null>,
    bridge: OrdinalWalletBridgeProps | null | undefined,
    src: string,
) {
    const origin = bridge?.origin || null
    const provenance = useOrdinalProvenance(origin)
    const appearanceTheme = useStore(state => state.appearanceTheme)
    const { openRight } = useOpenRight()
    const openRightRef = useRef(openRight)
    openRightRef.current = openRight
    const postRef = useRef(bridge)
    postRef.current = bridge
    // User IDs behind the creator and owner the context names, so the ordinal can open them.
    const contextUserIds = useRef(new Map<string, string>())
    contextUserIds.current = new Map(
        [
            provenance?.chain?.creator?.user,
            provenance?.chain?.current_owner?.user,
            provenance?.owner,
        ]
            .filter(user => user?.id && (user.name || user.username))
            .map(user => [(user!.name || user!.username)!, String(user!.id)]),
    )

    const latestStop = useRef<(() => void) | undefined>(undefined)
    latestStop.current = bridge?.onStop
    const latest = useRef<OrdinalContextPayload | null>(null)
    latest.current = origin
        ? {
              ordinal: { origin },
              creator: actor(provenance?.chain?.creator),
              owner:
                  actor(provenance?.chain?.current_owner) ||
                  (provenance?.owner
                      ? {
                            username: provenance.owner.name || provenance.owner.username || null,
                            address: null,
                        }
                      : null),
              viewerIsOwner: provenance?.viewer_is_owner === true,
              post: bridge?.answerId
                  ? { answerId: bridge.answerId, threadId: bridge.threadId || null }
                  : null,
              theme: resolveEffectiveTheme(appearanceTheme),
          }
        : null

    useLayoutEffect(() => {
        const iframe = iframeRef.current
        if (!origin || !iframe) return
        const unregister = registerOrdinalFrame(iframe, {
            origin,
            label: () => {
                const creator = latest.current?.creator?.username
                return creator ? `Ordinal by @${creator}` : `Ordinal ${origin.slice(0, 8)}…`
            },
            context: () => latest.current!,
            stop: () => latestStop.current?.(),
            contextUserId: username => contextUserIds.current.get(username) ?? null,
            openProfile: userId => openRightRef.current(withDepth({ user: userId })),
            openPost: () => {
                const { answerId, threadId } = postRef.current || {}
                if (answerId && threadId) openRightRef.current({ questId: threadId, answerId })
            },
        })
        iframe.setAttribute('src', src)
        return unregister
    }, [iframeRef, origin, src])
}
